import {FC, useEffect, useState} from 'react';
import {BODY1, defaultPage, defaultPageSize, DD_MM_YYYY_HH_MM_DOT, SUBTITLE2, CENTER, NUMBER} from '@/utils/constants';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Pagination from '@/shared/components/Pagination';
import {ReactComponent as ArrowUpDownIcon} from '@icons/arrow-up-down.svg';
import {ReactComponent as DeactivateIcon} from '@icons/shield-cross.svg';
import {ReactComponent as RestoreIcon} from '@icons/shield-tick.svg';
import {ReactComponent as ArrowDown} from '@icons/arrow-down.svg';
import organization from '@images/organization-example.png';
import Input from '@/shared/components/Inputs/Input';
import Modal from '@/shared/components/Modal';
import OrganizationDetails from './OrganizationDetails';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getOrganizationsListSelector} from '@/store/admin/selector';
import {getAdminOrganizationById, getAllOrganizations} from '@/store/admin';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {IAdminOrganizationResponse, IOrganizationStatus} from '@/utils/types/superAdmin.interface';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Loader from '@/shared/components/Loader';
import {organizationsTableStyle} from './OrganizationTableStyle.styles';
import {getTeamMembers} from '@/store/orgatization-team';
import {FilterActivity, FilterRole, ITeamData} from '@/utils/types';
import dayjs from 'dayjs';
import {ITeamRole} from '@/utils/enums';
import {SuperAdminService} from '@/utils/services';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

export interface TableData {
  id: number;
  name: string;
  email: string;
  phone: string;
  transactionPercent: string;
  status: 'Active' | 'Deactivated';
}

export interface IFilterGroup {
  title: string;
  options: IFilterOption[];
}

export interface IFilterOption {
  title: string;
  isSelected: boolean;
  isCheckBox?: boolean;
  id?: number | null;
  value?: string | null;
}

export enum IAdminOrganizationStatus {
  All = 'All',
  Active = 'Active',
  Deactivated = 'Deactivated',
}

export const defaultFilterOptions: IFilterGroup[] = [
  {
    title: 'Status',
    options: [
      {title: 'All', isSelected: true, value: 'All'},
      {title: 'Active', isSelected: false, value: 'Active'},
      {title: 'Deactivated', isSelected: false, value: 'Deactivated'},
    ],
  },
];
// 👇 columnOptions теперь с key + label
const columnOptions: {key: keyof TableData; label: string}[] = [
  {key: 'id', label: 'ID'},
  {key: 'name', label: 'Organization name'},
  {key: 'email', label: 'Email'},
  {key: 'phone', label: 'Phone number'},
  {key: 'transactionPercent', label: 'Transac. %'},
  {key: 'status', label: 'Status'},
];

const Organizations: FC = () => {
  const dispatch = useAppDispatch();

  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState<number>(defaultPage);
  const [filterOptions, setFilterOptions] = useState<IFilterGroup[]>(defaultFilterOptions);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<IAdminOrganizationResponse | null>(null);
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [membersMap, setMembersMap] = useState<Record<number, ITeamData[]>>({});
  const [membersLoading, setMembersLoading] = useState<number | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [isOrderDesc, setIsOrderDesc] = useState(false);

  const {currentPage, pageSize, totalCount, data} = useAppSelector(getOrganizationsListSelector) ?? {
    currentPage: 1,
    totalPages: 0,
    pageSize: defaultPageSize,
    totalCount: 0,
    data: [],
  };

  const {apiInstance} = useApiInstance();

  const handleFilterChange = (groupTitle: string, optionValue: string) => {
    setPage(1);
    const updatedOptions = filterOptions.map((group) => {
      if (group.title !== groupTitle) return group;

      return {
        ...group,
        options: group.options.map((opt) => ({
          ...opt,
          isSelected: opt.value === optionValue,
        })),
      };
    });

    setFilterOptions(updatedOptions);
  };

  const handleSearchChange = (name: string) => {
    setSearchName(name);
    setPage(1);
  };

  const getStatusSx = (status: string) => {
    if (status === IAdminOrganizationStatus.Deactivated) return organizationsTableStyle.statusDeactivated;
    return organizationsTableStyle.statusActive;
  };

  const getLastActiveSx = (status: string) => {
    if (status.includes('Active')) return organizationsTableStyle.lastActiveDate;
    if (status.includes('Invited') || status.includes('Inactive')) return organizationsTableStyle.lastActiveInvited;
    return organizationsTableStyle.lastActiveDate;
  };

  const handleSortChange = (newSortColumn: string) => () => {
    setSortColumn(newSortColumn);
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;

    setIsOrderDesc(isAsc);
    setPage(1);
  };

  const handleRestoreOrganization = async () => {
    try {
      await SuperAdminService.updateOrganizationStatus({id: selectedOrganization?.id || 0, newStatus: 2});
      await fetchOrganizations();
      setIsChangeStatusModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleDeactivateOrganization = async () => {
    try {
      await SuperAdminService.updateOrganizationStatus({id: selectedOrganization?.id || 0, newStatus: 3});
      await fetchOrganizations();
      setIsChangeStatusModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateFeeNumber = async (id: number, fee: string) => {
    try {
      await SuperAdminService.setOrganizationPlatformFee(id || 0, {
        transactionFee: Number(fee),
      });
      await fetchOrganizations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fetchOrganizations = async () => {
    setIsDataLoaded(false);
    try {
      const data = {
        page,
        pageSize: defaultPageSize,
        ...(searchName && {searchName}),
        isOrderDesc,
        sortColumn: sortColumn ?? '',
        status: (filterOptions[0].options.find((opt) => opt.isSelected)?.value || 'All') as IOrganizationStatus,
      };
      await dispatch(getAllOrganizations(data)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
    setIsDataLoaded(true);
  };

  const fetchOrganization = async (id: number) => {
    try {
      const data = await dispatch(getAdminOrganizationById(id)).unwrap();
      setSelectedOrganization(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handlePressEnter = () => {
    setPage(defaultPage);
  };

  const fetchOrganizationMembers = async (id: number) => {
    if (membersMap[id]) return;

    setMembersLoading(id);

    const memberDto = {
      page: 1,
      pageSize: 1000,
      searchName: '',
      role: 'AllRoles' as FilterRole,
      activity: ' AllUsers' as FilterActivity,
      organizationId: id!,
    };
    try {
      const {data} = await dispatch(getTeamMembers({reqBody: memberDto, apiInstance})).unwrap();
      setMembersMap((prev) => ({...prev, [id]: data}));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setMembersLoading(null);
    }
  };

  const handleToggleRow = (id: number) => {
    if (selectedId === id) {
      setSelectedId(null);
    } else {
      setSelectedId(id);
      fetchOrganizationMembers(id);
    }
  };

  const onSuccess = async () => {
    await fetchOrganizations();
    await fetchOrganization(selectedOrganization?.id || 0);
  };

  useEffect(() => {
    fetchOrganizations();
  }, [searchName, page, filterOptions, isOrderDesc, sortColumn]);

  return (
    <Box sx={organizationsTableStyle.wrapper}>
      <SearchHeader
        page={page}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        filterOption={filterOptions}
      />

      {isDataLoaded ? (
        totalCount ? (
          <Box sx={organizationsTableStyle.tableContent}>
            <TableContainer sx={organizationsTableStyle.container}>
              <Table sx={organizationsTableStyle.table}>
                <TableHead>
                  <TableRow sx={organizationsTableStyle.tableHeader}>
                    {columnOptions.map(({key, label}) => {
                      const sortable = key === 'id' || key === 'name';

                      return (
                        <TableCell key={key}>
                          <Box sx={organizationsTableStyle.headerCellTitle}>
                            {label}
                            {sortable && (
                              <Box sx={organizationsTableStyle.sortIcon} onClick={handleSortChange(key)}>
                                <ArrowUpDownIcon />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody sx={organizationsTableStyle.tableBody}>
                  {data.map((row) => (
                    <>
                      <TableRow
                        key={row.id}
                        sx={[
                          selectedId === row.id ? organizationsTableStyle.selectedRow : {},
                          {'& > *': {borderBottom: selectedId === row.id ? 'unset' : undefined}},
                        ]}
                      >
                        {columnOptions.map(({key}) => {
                          if (key === 'name') {
                            return (
                              <TableCell key={key}>
                                <Box sx={organizationsTableStyle.nameCell}>
                                  <Box sx={organizationsTableStyle.avatar}>
                                    <img src={row.logo ? row.logo : organization} alt="avatar" />
                                  </Box>
                                  <Typography sx={organizationsTableStyle.name}>{row.name}</Typography>
                                </Box>
                              </TableCell>
                            );
                          }

                          if (key === 'id') {
                            return (
                              <TableCell key={key} sx={organizationsTableStyle.textID}>
                                {row.id}
                              </TableCell>
                            );
                          }

                          if (key === 'transactionPercent') {
                            return (
                              <TableCell key={key}>
                                <Input
                                  defaultValue={row.transactionFee.toString()}
                                  onBlur={(event) => {
                                    handleUpdateFeeNumber(row.id, event.target.value);
                                  }}
                                  type={NUMBER}
                                />
                              </TableCell>
                            );
                          }

                          if (key === 'status') {
                            return (
                              <TableCell key={key}>
                                <Typography sx={getStatusSx(row.organizationStatus)}>
                                  {row.organizationStatus === IAdminOrganizationStatus.Deactivated
                                    ? 'Deactivated'
                                    : 'Active'}
                                </Typography>
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={key} sx={organizationsTableStyle.email}>
                              <Typography>{row[key]}</Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell sx={organizationsTableStyle.menu}>
                          <ActionsMenu
                            content={
                              <Box>
                                <Typography
                                  sx={organizationsTableStyle.menuItem}
                                  onClick={() => {
                                    setIsOrganizationModalOpen(true);
                                    fetchOrganization(row.id);
                                  }}
                                  variant={BODY1}
                                >
                                  Details
                                </Typography>
                                <Typography
                                  sx={organizationsTableStyle.menuItem}
                                  onClick={() => {
                                    fetchOrganization(row.id);
                                    setIsChangeStatusModalOpen(true);
                                  }}
                                  variant={BODY1}
                                >
                                  {row.organizationStatus === IAdminOrganizationStatus.Deactivated
                                    ? 'Restore Account'
                                    : 'Deactivate Account'}
                                </Typography>
                              </Box>
                            }
                          />
                        </TableCell>
                        <TableCell sx={organizationsTableStyle.arrow} onClick={() => handleToggleRow(row.id)}>
                          {selectedId === row.id ? <ArrowDown style={{transform: 'rotate(180deg)'}} /> : <ArrowDown />}
                        </TableCell>
                      </TableRow>
                      {selectedId === row.id && (
                        <TableRow>
                          <TableCell sx={{p: '0 !important', border: 'none'}} colSpan={columnOptions.length + 2}>
                            <Box sx={organizationsTableStyle.membersWrapper}>
                              {membersLoading === row.id ? (
                                <Loader parentClassName={organizationsTableStyle.loader} size={30} />
                              ) : membersMap[row.id]?.length ? (
                                <Table size="small" sx={{p: 0}}>
                                  <colgroup>
                                    <col style={{width: '65%'}} />
                                    <col style={{width: '20%'}} />
                                    <col style={{width: '15%'}} />
                                  </colgroup>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell align={CENTER} sx={organizationsTableStyle.membersHeaderCell}>
                                        Member
                                      </TableCell>
                                      <TableCell align={CENTER} sx={organizationsTableStyle.membersHeaderCell}>
                                        Last Activity
                                      </TableCell>
                                      <TableCell sx={organizationsTableStyle.membersHeaderCell}>Role</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody sx={{p: 0}}>
                                    {membersMap[row.id].map((member) => (
                                      <TableRow key={member.id}>
                                        <TableCell>
                                          <Box sx={organizationsTableStyle.nameCell}>
                                            <Box sx={organizationsTableStyle.avatar}>
                                              <img src={member.logo ? member.logo : organization} alt="avatar" />
                                            </Box>
                                            <Box>
                                              <Typography
                                                sx={organizationsTableStyle.name}
                                              >{`${member.firstName} ${member.lastName}`}</Typography>
                                              <Typography sx={organizationsTableStyle.email}>{member.email}</Typography>
                                            </Box>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="left">
                                          <Typography sx={getLastActiveSx(member.status)}>
                                            {member.lastActive
                                              ? dayjs.utc(member.lastActive).local().format(DD_MM_YYYY_HH_MM_DOT)
                                              : 'Invitation sent'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant={SUBTITLE2}
                                            sx={
                                              member.role === ITeamRole.OWNER
                                                ? organizationsTableStyle.owner
                                                : organizationsTableStyle.member
                                            }
                                          >
                                            {member.role}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <EmptyTableData text="No Members Found" />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={organizationsTableStyle.paginationContainer}>
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(step) => setPage(step)}
              />
            </Box>
          </Box>
        ) : (
          <EmptyTableData text="No Organizations Found" />
        )
      ) : (
        <Loader parentClassName={organizationsTableStyle.loader} size={50} />
      )}

      <Modal
        mainButtonTitle={
          selectedOrganization?.organizationStatus === IAdminOrganizationStatus.Deactivated
            ? 'Restore Account'
            : 'Deactivate Account'
        }
        closeButtonTitle="Cancel"
        open={isChangeStatusModalOpen}
        onClose={() => setIsChangeStatusModalOpen(false)}
        onSave={
          selectedOrganization?.organizationStatus === IAdminOrganizationStatus.Deactivated
            ? handleRestoreOrganization
            : handleDeactivateOrganization
        }
        mainIcon={
          selectedOrganization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? (
            <RestoreIcon />
          ) : (
            <DeactivateIcon />
          )
        }
        sx={organizationsTableStyle.statusModal}
        title={
          selectedOrganization?.organizationStatus === IAdminOrganizationStatus.Deactivated
            ? 'Restore Organization'
            : 'Deactivate Organization'
        }
        color={selectedOrganization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? 'primary' : 'error'}
        isShowCloseButton
        isShowSaveButton
      >
        {selectedOrganization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? (
          <Box sx={organizationsTableStyle.textWrapper}>
            <Typography sx={organizationsTableStyle.description}>
              <Typography sx={organizationsTableStyle.bold}>{selectedOrganization?.email}</Typography> will be restored.
              <Typography sx={organizationsTableStyle.bold}>{selectedOrganization?.name}</Typography> will be notified
              via email. It may take up to 30 minutes for the changes to take place.
            </Typography>

            <Typography sx={organizationsTableStyle.descriptionBottom}>
              Are you sure you want to restore this account?
            </Typography>
          </Box>
        ) : (
          <Box sx={organizationsTableStyle.textWrapper}>
            <Typography sx={organizationsTableStyle.boldCenter}>{selectedOrganization?.email}</Typography>
            <Typography sx={organizationsTableStyle.description}>
              will no longer be able to access to Event Simpli.
            </Typography>
            <Typography sx={organizationsTableStyle.descriptionBottom}>
              Are you sure you want to deactivate this account?
            </Typography>
          </Box>
        )}
      </Modal>

      <Modal
        open={isOrganizationModalOpen}
        onClose={() => setIsOrganizationModalOpen(false)}
        sx={organizationsTableStyle.detailsModal}
      >
        <OrganizationDetails
          organization={selectedOrganization}
          onClose={() => setIsOrganizationModalOpen(false)}
          onSuccess={onSuccess}
        />
      </Modal>
    </Box>
  );
};

export default Organizations;
